import { LayerData } from '@/atoms';
import { LCCRender } from '@/lib/lcc-0.5.4';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const RESOURCES_ENDPOINT = import.meta.env.VITE_RESOURCES_ENDPOINT as string;

interface LCCObject {}

export const Xgrids = ({ layers }: { layers: LayerData[] }) => {
  const { scene, camera, gl } = useThree();
  const lccObjectsRef = useRef<Map<string, LCCObject>>(new Map());

  // Patch fetch, XMLHttpRequest, and Worker to include credentials for resource requests
  useEffect(() => {
    // Patch main-thread fetch
    const originalFetch = window.fetch;
    window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      if (url.startsWith(RESOURCES_ENDPOINT)) {
        init = { ...init, credentials: 'include' };
      }
      return originalFetch.call(this, input, init);
    };

    // Patch main-thread XMLHttpRequest
    const originalOpen = XMLHttpRequest.prototype.open;
    // @ts-expect-error - overriding open signature
    XMLHttpRequest.prototype.open = function (...args: Parameters<typeof originalOpen>) {
      originalOpen.apply(this, args);
      const url = args[1];
      if (typeof url === 'string' && url.startsWith(RESOURCES_ENDPOINT)) {
        this.withCredentials = true;
      }
    };

    // todo: patch worker in lcc always!!!
    // Patch Worker constructor to inject credentials only for resource-related workers
    // const credentialPatchScript = `
    //   var __RE__ = ${JSON.stringify(RESOURCES_ENDPOINT)};
    //   var __origFetch__ = self.fetch;
    //   self.fetch = function(input, init) {
    //     var url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    //     if (url.startsWith(__RE__)) init = Object.assign({}, init, { credentials: 'include' });
    //     return __origFetch__.call(this, input, init);
    //   };
    //   if (typeof XMLHttpRequest !== 'undefined') {
    //     var __origOpen__ = XMLHttpRequest.prototype.open;
    //     XMLHttpRequest.prototype.open = function() {
    //       __origOpen__.apply(this, arguments);
    //       if (typeof arguments[1] === 'string' && arguments[1].startsWith(__RE__)) this.withCredentials = true;
    //     };
    //   }
    // `;
    // class PatchedWorker extends window.Worker {
    //   constructor(scriptURL: string | URL, options?: WorkerOptions) {
    //     const url = typeof scriptURL === 'string' ? scriptURL : scriptURL.toString();

    //     // Don’t trust console.log(options) — check the actual property
    //     const type = options?.type; // "classic" | "module" | undefined
    //     const abs = new URL(url, document.baseURI).toString();

    //     const loader =
    //       type === 'module' ? `import ${JSON.stringify(abs)};\n` : `importScripts(${JSON.stringify(abs)});\n`;

    //     const blob = new Blob([loader + credentialPatchScript], {
    //       type: 'application/javascript',
    //     });

    //     const blobUrl = URL.createObjectURL(blob);
    //     super(blobUrl, options);
    //   }
    // }

    return () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalOpen;
    };
  }, []);

  useEffect(() => {
    const currentObjects = lccObjectsRef.current;
    let cancelled = false;

    // Clean up previous lcc objects
    // for (const [, lccObject] of currentObjects) {
    //   lccObject.unload();
    // }
    currentObjects.clear();

    layers.forEach((layer) => {
      if (!layer.root) return;

      const dataPath = `${RESOURCES_ENDPOINT}/${layer.root}`;

      const lccObject = LCCRender.load(
        {
          camera,
          scene,
          dataPath,
          renderLib: THREE,
          renderer: gl,
          canvas: gl.domElement,
        },
        (mesh) => {
          console.log('XGRIDS', mesh);
          if (cancelled) {
            scene.remove(mesh);
            return;
          }
        },
        (percent) => {
          console.log(`XGRIDS ${layer.root}: ${(percent * 100).toFixed(0)}%`);
        },
        () => {
          console.error(`Failed to load XGRIDS model: ${layer.root}`);
        },
      ) as LCCObject;

      currentObjects.set(layer.id, lccObject);
    });

    return () => {
      cancelled = true;
      for (const [, lccObject] of currentObjects) {
        LCCRender.unload(lccObject);
      }
      currentObjects.clear();
    };
  }, [layers, scene, camera, gl]);

  useFrame(() => {
    LCCRender.update();
  });

  return null;
};
